import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AxiosError } from 'axios';

import { signIn, refreshToken } from '@apis/auth';
import { authState } from '@recoils/atoms';
import { tokenStorage } from '@utils/tokenStorage';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '@constants/token';

type LoginPayload = {
  username: string;
  password: string;
  rememberMe?: boolean;
};

/**
 * useAuth
 *
 * Centralized authentication hook.
 * Responsibilities:
 * - Handle login/logout flows
 * - Manage auth state (Recoil)
 * - Refresh access tokens when possible
 *
 * Navigation and session-expired redirects are handled
 * by route guards (AuthRoute/AdminRoute) and API interceptors.
 */
function useAuth() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] =
    useRecoilState<null | boolean>(authState);

  /**
   * Login with username/password.
   * Converts backend errors into human-readable messages.
   */
  const login = async ({
    username,
    password,
    rememberMe = true,
  }: LoginPayload) => {
    try {
      setLoading(true);

      const { data } = await signIn({ username, password });
      const accessToken = data[0].accessToken;
      const refreshTokenValue = data[0].refreshToken;

      // Store tokens based on "Remember me" policy
      const storage = rememberMe ? window.localStorage : window.sessionStorage;
      storage.setItem(TOKEN_KEY, accessToken);
      storage.setItem(REFRESH_TOKEN_KEY, refreshTokenValue);

      setIsAuthenticated(true);
    } catch (err) {
      const e = err as AxiosError<any>;
      const status = e.response?.status;

      // Translate backend status codes into user-friendly messages
      if (status === 401) {
        throw new Error('Invalid username or password.');
      }
      if (status === 403) {
        throw new Error('Access denied. Please contact your administrator.');
      }
      if (status === 429) {
        throw new Error('Too many attempts. Please try again later.');
      }

      throw new Error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh access token using the refresh token.
   * If refresh fails, the session is considered expired.
   */
  const refresh = async () => {
    const refreshTokenValue = tokenStorage.getRefreshToken();
    if (!refreshTokenValue) return;

    try {
      const { data } = await refreshToken(refreshTokenValue);
      tokenStorage.setAccessToken(data[0]);
    } catch {
      // Refresh failure = session expired
      logOut();
    }
  };

  /**
   * Logout the user and clear all authentication tokens.
   */
  const logOut = () => {
    tokenStorage.clearAll();
    setIsAuthenticated(false);
    navigate('/login');
  };

  /**
   * Initialize auth state from existing tokens.
   * This runs once on mount to restore session state.
   */
  useEffect(() => {
    const token = tokenStorage.getAccessToken();
    setIsAuthenticated(!!token);
  }, [setIsAuthenticated]);

  return {
    loading,
    isAuthenticated,
    login,
    logOut,
    refresh,
  };
}

export default useAuth;
